import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, UserMinus, X, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FriendsList({ userId, isOwner, onCountChange }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchFriends();
  }, [userId, search]);

  const fetchFriends = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const endpoint = isOwner 
        ? `${API}/friends` 
        : `${API}/friends/user/${userId}`;
      
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      
      const response = await axios.get(`${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data);
      onCountChange?.(response.data.length);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnfriend = async (friendId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to remove this friend?')) return;
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/friends/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devotee removed');
      fetchFriends();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  const displayedFriends = showAll ? friends : friends.slice(0, 6);

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-[#FF9933]" />
          <h3 className="font-semibold text-gray-900">Devotees</h3>
        </div>
        <div className="flex justify-center py-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#FF9933] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#FF9933]" />
          <h3 className="font-semibold text-gray-900">Devotees</h3>
          <span className="text-sm text-gray-500">({friends.length})</span>
        </div>
        {friends.length > 6 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-[#FF9933] hover:text-[#E68A00]"
          >
            {showAll ? 'Show Less' : 'See All'}
          </button>
        )}
      </div>

      {/* Search */}
      {friends.length > 3 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search friends..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
            data-testid="friends-search-input"
          />
        </div>
      )}

      {friends.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            {search ? 'No friends found' : 'No friends yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {displayedFriends.map((friend) => (
            <Link
              key={friend.id}
              to={`/profile/${friend.id}`}
              className="group relative bg-gray-50 rounded-lg p-3 hover:bg-orange-50 transition-colors text-center"
              data-testid={`friend-card-${friend.id}`}
            >
              {/* Avatar with animated placeholder fallback */}
              {friend.avatar && !friend.avatar.includes('ui-avatars.com') ? (
                <img
                  src={friend.avatar}
                  alt={friend.name}
                  className="w-16 h-16 rounded-full object-cover mx-auto mb-2"
                />
              ) : (
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)',
                    backgroundSize: '200% 200%',
                    animation: 'avatarShimmer 2s ease-in-out infinite'
                  }}
                >
                  <User className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <p className="text-sm font-medium text-gray-900 truncate">{friend.name}</p>
              <p className="text-xs text-gray-500 capitalize">{friend.role}</p>
              
              {/* Remove friend button - only for owner */}
              {isOwner && (
                <button
                  onClick={(e) => handleUnfriend(friend.id, e)}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                  title="Remove friend"
                  data-testid={`unfriend-${friend.id}`}
                >
                  <X className="w-3 h-3 text-gray-500 hover:text-red-500" />
                </button>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
